import { useMutation } from '@tanstack/react-query';
import { useRoleMutationGate } from '@/api/_shared/roleGate';
import { apiPost } from '@/api/_shared/http';
import { parseStructuredNoteSections, type StructuredNoteSections } from '@/utils/structuredNoteSchema';
import { STRUCTURE_NOTE_INSTRUCTIONS } from './structureNotePrompt';
import { STRUCTURE_NOTE_RESPONSE_FORMAT } from './structureNoteSchema';
import type { CallLlmResponse, StructureNoteRequest } from './interfaces';

const BASE_URL = '/agents/api/v1/agent';

async function structureNoteRequest({ originalNoteContent }: StructureNoteRequest): Promise<StructuredNoteSections> {
  const response = await apiPost<CallLlmResponse>(`${BASE_URL}/llm`, {
    prompt: originalNoteContent,
    instructions: STRUCTURE_NOTE_INSTRUCTIONS,
    llmModel: 'gpt-4o-mini',
    temperature: 0.2,
    responseFormat: STRUCTURE_NOTE_RESPONSE_FORMAT,
  });

  let parsed: unknown;
  try {
    parsed = JSON.parse(response.content);
  } catch {
    throw new Error('AI returned invalid JSON');
  }

  const sections = parseStructuredNoteSections(parsed);
  if (!sections) {
    throw new Error('AI returned an unexpected note structure');
  }

  return sections;
}

export function useStructureNoteMutation() {
  const { assertReady } = useRoleMutationGate();

  return useMutation({
    mutationFn: async (request: StructureNoteRequest) => {
      assertReady();
      return structureNoteRequest(request);
    },
  });
}
